import { useEffect, useRef } from 'react';

import type { QuestionRunProgress } from 'src/components/ai/chat/ChatPanelMarkdown/elements/CodeElement/analysis-question-run';
import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';

const POLL_MS = 5_000;

/**
 * How far the report's running AI questions have got, as documents done of
 * the run's total. `progress` is null while nothing runs and before the first
 * count lands.
 *
 * The server only knows when the oldest still-running answer started, which
 * moves later as old answers finish. So for one run the total only grows and
 * the start only moves earlier: the bar never slides back, and documents
 * queued mid-run widen it.
 */
export function useReportRunProgress({
  filterModel,
  reportId,
  runningQuestionIds,
}: {
  filterModel: unknown;
  reportId: string;
  runningQuestionIds: string[];
}): { loading: boolean; progress: QuestionRunProgress | null } {
  const isRunning = runningQuestionIds.length > 0;
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const { data, loading, previousData, startPolling, stopPolling } =
    Apollo.useGetReportRunProgressQuery({
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !isRunning,
      variables: {
        filterModel: filterModel ?? null,
        filterTimeZone,
        questionIds: runningQuestionIds,
        reportId,
      },
    });

  useEffect(() => {
    if (!isRunning) return;
    startPolling(POLL_MS);
    return stopPolling;
  }, [isRunning, startPolling, stopPolling]);

  const runRef = useRef<{ maxTotal: number; startedAt: string | undefined } | null>(null);
  /**
   * A question finishing changes the query's variables, which clears `data`
   * until the new count lands; keep the last count of this run meanwhile.
   */
  const latest = data ?? (runRef.current ? previousData : undefined);
  const counts = isRunning ? latest?.report?.runProgress : null;
  if (!counts) {
    if (!isRunning) runRef.current = null;
    return { loading, progress: null };
  }

  const total = Math.max(runRef.current?.maxTotal ?? 0, counts.doneCount + counts.remainingCount);
  const startedAt = getEarlier(runRef.current?.startedAt, counts.startedAt ?? undefined);
  runRef.current = { maxTotal: total, startedAt };
  return {
    loading,
    progress: { done: total - counts.remainingCount, queuedAt: startedAt, total },
  };
}

function getEarlier(a: string | undefined, b: string | undefined) {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(a) <= Date.parse(b) ? a : b;
}
